import React, { useCallback, useMemo } from 'react';
import { Input } from '@chaser/components';
import MappingTableRow from '../../TableRow';
import LabeledBlock from '../../../../../../components/LabeledBlock';
import ChaserStyledSelect from '../../../../../../components/ChaserStyledSelect';
import { useAvailableColumnsAsOptions } from '../Simple';
import { useReadOnly } from '../../readOnlyContext';

export const getConsumedColumns = value => {
  return {
    [value.mapping.columnId]: true,
    ...Object.fromEntries((value.mapping.columns || []).map(c => [c.columnId, true])),
  };
};

const options = [
  { label: 'Single column with separator', value: 'SingleColumn' },
  { label: 'Multiple columns', value: 'MultipleColumns' },
];

const MappingTypeTags = ({ field, value, availableColumns, onChange }) => {
  const storageType = useMemo(() => {
    const type = (value?.mapping?.__typename || '').replace('CSVMappingFieldValueTagsMapping', '');
    return options.find(option => option.value === type);
  }, [value]);

  const onStorageTypeChange = useCallback(
    type => {
      const typeValue = type?.value;

      if (!typeValue) {
        onChange(null);
        return;
      }

      onChange({
        mapping: {
          __typename: `CSVMappingFieldValueTagsMapping${typeValue}`,
          ...(typeValue === 'SingleColumn' && { columnId: '', separator: ';' }),
          ...(typeValue === 'MultipleColumns' && { columns: [] }),
        },
      });
    },
    [onChange],
  );

  const onSeparatorChange = useCallback(
    e => {
      onChange({
        mapping: {
          ...value.mapping,
          separator: e.target.value,
        },
      });
    },
    [onChange, value],
  );

  const columnOptions = useAvailableColumnsAsOptions(availableColumns);

  const columnValue = useMemo(() => {
    const columns = value?.mapping?.columns?.map(c => c.columnId) || [value?.mapping?.columnId];
    return columns.filter(Boolean).map(c => ({ value: c, label: availableColumns[c]?.label || c }));
  }, [value, availableColumns]);

  const onColumnChange = useCallback(
    columnOrColumns => {
      onChange({
        mapping: {
          ...value.mapping,
          ...(storageType.value === 'SingleColumn' && { columnId: columnOrColumns?.value || null }),
          ...(storageType.value === 'MultipleColumns' && {
            columns: columnOrColumns?.map(c => ({ columnId: c?.value })) || [],
          }),
        },
      });
    },
    [onChange, value, storageType],
  );

  const readOnly = useReadOnly();

  return (
    <>
      <MappingTableRow field={field}>
        <LabeledBlock label="How are tags stored?">
          <ChaserStyledSelect
            options={options}
            isClearable={!field.required}
            value={storageType}
            required={field.required}
            onChange={onStorageTypeChange}
            isDisabled={readOnly}
          />
        </LabeledBlock>
      </MappingTableRow>

      {storageType?.value === 'SingleColumn' && (
        <MappingTableRow field={{ name: 'Separator' }} key="single-column-separator" isNested>
          <Input
            width="full"
            value={value?.mapping?.separator}
            onChange={onSeparatorChange}
            disabled={readOnly}
          />
        </MappingTableRow>
      )}

      {storageType?.value && (
        <MappingTableRow
          field={{ name: `Column${storageType.value === 'SingleColumn' ? '' : 's'}` }}
          key={storageType.value}
          isNested
        >
          <ChaserStyledSelect
            options={columnOptions}
            value={columnValue}
            required={true}
            isMulti={storageType.value === 'MultipleColumns'}
            onChange={onColumnChange}
            isDisabled={readOnly}
          />
        </MappingTableRow>
      )}
    </>
  );
};

export const Component = MappingTypeTags;
